import { ArrowRight, Calendar } from "lucide-react";
import { Link } from "react-router-dom";
import { Reveal } from "@/components/Reveal";
import { useCalendly } from "@/components/CalendlyButton";

const FOUNDER_PORTRAIT = "/founder/jenna-studio.jpeg";
const FOUNDER_OFFICE = "/founder/jenna-street.jpeg";

const idealFor = [
  "You&rsquo;ve already proven you can succeed — and you&rsquo;re asking what you want next.",
  "You&rsquo;re navigating a meaningful career or leadership inflection point.",
  "You&rsquo;re still ambitious, but increasingly intentional about where you invest your time and energy.",
  "You want to understand and advocate for the value your leadership creates.",
  "You want your next chapter to reflect both the leader you&rsquo;ve become and the life you&rsquo;re building.",
];

const experience = [
  { key: "Clarity", desc: "Define what you want next and the outcomes that matter." },
  { key: "Value", desc: "Understand the impact you create and strengthen how you position and advocate for it." },
  { key: "Strategy", desc: "Make intentional decisions about leadership, career, opportunity and the life you&rsquo;re building." },
  { key: "Action", desc: "Turn clarity into priorities and a practical plan for moving forward." },
];

export default function Lead() {
  const openCalendly = useCalendly();

  return (
    <main data-testid="lead-page" className="bg-cream">
      {/* HERO */}
      <section className="pt-40 md:pt-52 pb-20 md:pb-28">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          <div className="lg:col-span-7">
            <Reveal>
              <p className="text-xs uppercase tracking-[0.4em] text-terracotta mb-8">
                Private Leadership &amp; Career Strategy
              </p>
              <h1 className="font-serif font-light text-wine text-4xl sm:text-5xl md:text-6xl leading-[1.05] tracking-tight">
                Get intentional about
                <span className="block text-terracotta mt-2">what comes next.</span>
              </h1>
              <p className="mt-8 max-w-2xl text-lg md:text-xl text-charcoal/80 leading-relaxed">
                A private 90-day coaching experience for accomplished mothers navigating a meaningful career or
                leadership inflection point.
              </p>
              <p className="mt-6 max-w-2xl text-lg text-charcoal/80 leading-relaxed">
                Together, we&rsquo;ll clarify what you want next, strengthen how you articulate and advocate
                for the value you create, and build an outcome-led strategy for moving forward.
              </p>
            </Reveal>
            <Reveal delay={0.25}>
              <div className="mt-10">
                <button
                  onClick={() => openCalendly({ medium: "hero", content: "primary-cta" })}
                  data-testid="hero-book-cta"
                  className="group inline-flex items-center gap-3 rounded-full bg-terracotta text-cream px-8 py-4 text-sm font-medium tracking-wide hover:bg-wine transition-colors duration-300"
                >
                  <Calendar size={16} />
                  Book a Discovery Call
                  <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
                </button>
              </div>
            </Reveal>
          </div>

          <div className="lg:col-span-5 hidden lg:block">
            <Reveal delay={0.2}>
              <div className="relative aspect-[4/5] overflow-hidden">
                <img
                  src={FOUNDER_OFFICE}
                  alt="Jenna Martins in an executive setting"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-charcoal/40 via-transparent to-transparent" />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* WHO IT'S FOR */}
      <section className="py-20 md:py-28 bg-blush/30" data-testid="who-its-for-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <Reveal>
              <p className="text-xs uppercase tracking-[0.3em] text-terracotta">Who It&rsquo;s For</p>
              <h2 className="font-serif font-light text-wine text-3xl md:text-4xl mt-4 leading-tight">
                You&rsquo;ve already proven you can succeed.
              </h2>
              <p className="mt-6 font-serif italic text-xl text-charcoal/70 leading-snug max-w-md">
                You&rsquo;re not looking for someone to teach you how to be ambitious. You&rsquo;re looking for
                the space and strategic perspective to determine what you want from your next chapter — and
                how to get there.
              </p>
            </Reveal>
          </div>
          <div className="lg:col-span-7">
            <Reveal delay={0.1}>
              <ul className="space-y-0 border-t border-gold/40">
                {idealFor.map((line, i) => (
                  <li
                    key={i}
                    className="flex items-start gap-4 py-5 border-b border-gold/40"
                    data-testid={`ideal-for-item-${i}`}
                  >
                    <span className="font-serif text-terracotta text-lg pt-0.5 tabular-nums">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <span
                      className="text-charcoal font-serif text-lg leading-snug"
                      dangerouslySetInnerHTML={{ __html: line }}
                    />
                  </li>
                ))}
              </ul>
            </Reveal>
          </div>
        </div>
      </section>

      {/* THE 90-DAY EXPERIENCE */}
      <section className="py-24 md:py-32 bg-wine text-cream" data-testid="experience-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.3em] text-blush">The 90-Day Experience</p>
            <h2 className="font-serif font-light text-cream text-3xl md:text-5xl mt-4 leading-tight max-w-3xl">
              90 days. One clear objective: move forward with intention.
            </h2>
          </Reveal>

          <div
            className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8"
            data-testid="experience-grid"
          >
            {experience.map((step, i) => (
              <Reveal key={step.key} delay={i * 0.08}>
                <div className="border border-cream/20 p-8 h-full hover:border-blush/60 transition-colors">
                  <p className="text-xs text-blush uppercase tracking-[0.3em]">
                    {String(i + 1).padStart(2, "0")}
                  </p>
                  <h3 className="mt-6 font-serif font-light text-3xl text-cream">{step.key}</h3>
                  <p
                    className="mt-4 text-cream/75 leading-relaxed"
                    dangerouslySetInnerHTML={{ __html: step.desc }}
                  />
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* WHAT YOU'LL WALK AWAY WITH */}
      <section className="py-20 md:py-28" data-testid="walk-away-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <Reveal>
              <p className="text-xs uppercase tracking-[0.3em] text-terracotta">
                What You&rsquo;ll Walk Away With
              </p>
              <h2 className="font-serif font-light text-wine text-3xl md:text-4xl mt-4 leading-tight">
                A clearer direction for what comes next — and a strategy for pursuing it.
              </h2>
            </Reveal>
          </div>
          <div className="lg:col-span-7 space-y-6 text-lg text-charcoal/80 leading-relaxed">
            <Reveal>
              <p>
                Because every woman&rsquo;s inflection point is different, the work is tailored to what
                you&rsquo;re navigating.
              </p>
            </Reveal>
            <Reveal delay={0.1}>
              <p>
                Depending on your goals, that may include greater clarity around your next career move, stronger
                positioning of your leadership value, a strategy for advancement or compensation conversations,
                clearer boundaries around where you invest your time and energy, and a practical plan for
                moving forward.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* MEET JENNA — SHORT CREDIBILITY */}
      <section className="py-20 md:py-28 bg-blush/30" data-testid="meet-jenna-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5">
            <Reveal>
              <div className="aspect-[4/5] overflow-hidden max-w-md">
                <img src={FOUNDER_PORTRAIT} alt="Jenna Martins" className="w-full h-full object-cover" />
              </div>
            </Reveal>
          </div>
          <div className="lg:col-span-7 space-y-6">
            <Reveal>
              <p className="text-xs uppercase tracking-[0.3em] text-terracotta">Meet Jenna</p>
              <h2 className="font-serif font-light text-wine text-3xl md:text-5xl mt-4 leading-tight">
                20+ years of leadership experience brought to your next chapter.
              </h2>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="text-lg text-charcoal/80 leading-relaxed">
                For more than 20 years, Jenna Martins built her career inside complex, high-performance
                organizations — leading strategy, growth, transformation, and high-stakes business
                initiatives.
              </p>
            </Reveal>
            <Reveal delay={0.15}>
              <p className="text-lg text-charcoal/80 leading-relaxed">
                Her work with The Thrive &amp; Mother Company™ brings that real-world leadership experience to
                helping accomplished women make pivotal decisions about their leadership, careers, and
                lives.
              </p>
            </Reveal>
            <Reveal delay={0.2}>
              <p className="text-sm text-charcoal/70">
                T&amp;M coaching is built around the Outcome-led Professional™ philosophy.{" "}
                <Link
                  to="/method"
                  data-testid="lead-method-link"
                  className="text-terracotta font-medium underline underline-offset-4 hover:text-wine transition-colors"
                >
                  Explore the T&amp;M Approach
                </Link>
                . Or{" "}
                <Link
                  to="/about"
                  data-testid="lead-about-link"
                  className="text-terracotta font-medium underline underline-offset-4 hover:text-wine transition-colors"
                >
                  read Jenna&rsquo;s full story
                </Link>
                .
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* CONVERSION */}
      <section className="py-24 md:py-32 bg-wine text-cream text-center" data-testid="lead-conversion">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.3em] text-blush">Ready To Talk About What&rsquo;s Next?</p>
            <h2 className="font-serif font-light text-cream text-3xl md:text-5xl mt-6 leading-tight">
              Book a complimentary 30-minute Discovery Call.
            </h2>
            <p className="mt-6 text-lg text-cream/80 leading-relaxed max-w-xl mx-auto">
              A confidential conversation with Jenna to explore where you are, what you&rsquo;re navigating,
              and whether working together may be a fit.
            </p>
            <button
              onClick={() => openCalendly({ medium: "conversion", content: "final-cta" })}
              data-testid="conversion-book-cta"
              className="group inline-flex items-center gap-3 mt-10 rounded-full bg-cream text-wine px-8 py-4 text-sm font-medium tracking-wide hover:bg-blush transition-colors duration-300"
            >
              <Calendar size={16} />
              Book a Discovery Call
              <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
            </button>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
